import {
	ArrowLeft,
	CheckCircle,
	Clipboard,
	Download,
	Edit3,
	HelpCircle,
	Maximize,
	Play,
	Redo,
	Save,
	Settings,
	Trash2,
	Undo,
	Upload,
	Zap,
	type LucideIcon,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReactFlow, type Edge, type Node } from 'reactflow';
import { useClipboard } from '../contexts/ClipboardContext';
import { useWorkflowContext } from '../contexts/WorkflowContext';
import { applyAutoLayout } from '../lib/auto-layout';
import { useDialog } from './DialogProvider';
import { HelpMenu } from './HelpMenu';

interface WorkflowMetadata {
	title: string;
	description: string;
	folderId?: string;
	createdAt: string;
	updatedAt: string;
	status?: 'draft' | 'published' | 'archived';
}

interface HeaderProps {
	workflowMetadata: WorkflowMetadata;
	onEditWorkflow: () => void;
	onResetWorkflow: () => void;
	onSaveWorkflow: () => void | Promise<void>;
}

type ActionMenuItem = {
	icon: LucideIcon;
	label: string;
	onClick: () => void;
	disabled?: boolean;
	className?: string;
};

type DividerMenuItem = {
	type: 'divider';
};

type MenuItem = ActionMenuItem | DividerMenuItem;

// Sample workflow for the "Load Sample" action
const sampleWorkflow = {
	nodes: [
		{
			id: 'start',
			type: 'start',
			position: { x: 100, y: 100 },
			data: { label: 'Start', category: 'flow-control', type: 'start' },
		},
		{
			id: 'api-1',
			type: 'api-call',
			position: { x: 100, y: 200 },
			data: {
				label: 'Fetch User Data',
				category: 'processing',
				type: 'api-call',
				config: {
					url: 'https://api.example.com/users',
					method: 'GET',
				},
			},
		},
		{
			id: 'transform-1',
			type: 'transform',
			position: { x: 100, y: 300 },
			data: {
				label: 'Process Data',
				category: 'processing',
				type: 'transform',
				config: {
					transformations: ['map', 'filter'],
				},
			},
		},
		{
			id: 'end',
			type: 'end',
			position: { x: 100, y: 400 },
			data: { label: 'End', category: 'flow-control', type: 'end' },
		},
	],
	edges: [
		{ id: 'e1-2', source: 'start', target: 'api-1' },
		{ id: 'e2-3', source: 'api-1', target: 'transform-1' },
		{ id: 'e3-4', source: 'transform-1', target: 'end' },
	],
};

export function Header({
	workflowMetadata,
	onEditWorkflow,
	onResetWorkflow,
	onSaveWorkflow,
}: HeaderProps) {
	const { alert, confirm } = useDialog();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
	const {
		getNodes,
		getEdges,
		setNodes,
		setEdges,
		fitView,
		deleteElements,
		getNode,
		addNodes,
		addEdges,
	} = useReactFlow();
	const { undo, redo, canUndo, canRedo } = useWorkflowContext();
	const { pasteNode, hasNodeInClipboard } = useClipboard();

	// Undo/Redo history management
	const [history, setHistory] = useState<{ nodes: Node[]; edges: Edge[] }[]>([]);
	const [currentStep, setCurrentStep] = useState(-1);

	const saveToHistory = useCallback(
		(nodes: Node[], edges: Edge[]) => {
			setHistory(prev => {
				// Remove any future states if we're not at the latest step
				const newHistory = prev.slice(0, currentStep + 1);
				return [
					...newHistory,
					{ nodes: nodes.map(n => ({ ...n })), edges: edges.map(e => ({ ...e })) },
				];
			});
			setCurrentStep(prev => prev + 1);
		},
		[currentStep]
	);

	// Save workflow to backend
	const handleSave = () => {
		onSaveWorkflow();
		setIsMenuOpen(false);
	};

	// Export workflow as JSON download
	const handleExportJson = () => {
		const flow = {
			nodes: getNodes(),
			edges: getEdges(),
			metadata: workflowMetadata,
		};
		const json = JSON.stringify(flow, null, 2);
		const blob = new Blob([json], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${workflowMetadata.title.toLowerCase().replace(/\s+/g, '-')}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		setIsMenuOpen(false);
	};

	// Load workflow from JSON file
	const handleLoadWorkflow = () => {
		const input = document.createElement('input');
		input.type = 'file';
		input.accept = '.json,application/json';

		input.onchange = async event => {
			const file = (event.target as HTMLInputElement).files?.[0];
			if (!file) return;

			try {
				const text = await file.text();
				const workflowData = JSON.parse(text);

				// Validate workflow structure
				if (!workflowData.nodes || !Array.isArray(workflowData.nodes)) {
					throw new Error('Invalid workflow format: missing or invalid nodes array');
				}

				if (!workflowData.edges || !Array.isArray(workflowData.edges)) {
					throw new Error('Invalid workflow format: missing or invalid edges array');
				}

				// Validate nodes have required properties
				for (const node of workflowData.nodes) {
					if (!node.id || !node.type || !node.position || !node.data) {
						throw new Error(
							`Invalid node format: node missing required properties (id, type, position, data)`
						);
					}
				}

				// Validate edges have required properties
				for (const edge of workflowData.edges) {
					if (!edge.id || !edge.source || !edge.target) {
						throw new Error(
							`Invalid edge format: edge missing required properties (id, source, target)`
						);
					}
				}

				// Check if canvas has existing content
				const existingNodes = getNodes();
				const existingEdges = getEdges();

				if (existingNodes.length > 0 || existingEdges.length > 0) {
					confirm({
						title: 'Replace Existing Workflow',
						message:
							'The canvas already contains a workflow. Do you want to replace it with the loaded workflow?\n\nThis action cannot be undone.',
						confirmText: 'Replace',
						cancelText: 'Keep Current',
						type: 'warning',
						onConfirm: () => {
							// Load the workflow
							setNodes(workflowData.nodes);
							setEdges(workflowData.edges);
							saveToHistory([], []);

							// Fit view after loading
							setTimeout(() => {
								fitView({ padding: 0.2 });
							}, 50);

							// Show success message
							const nodeCount = workflowData.nodes.length;
							const edgeCount = workflowData.edges.length;
							alert({
								title: 'Workflow Loaded Successfully',
								message: `Loaded ${nodeCount} nodes and ${edgeCount} connections.`,
								type: 'success',
							});
						},
						onCancel: () => {
							setIsMenuOpen(false);
						},
					});
					return;
				}

				// Load the workflow directly if canvas is empty
				setNodes(workflowData.nodes);
				setEdges(workflowData.edges);
				saveToHistory([], []);

				// Fit view after loading
				setTimeout(() => {
					fitView({ padding: 0.2 });
				}, 50);

				// Show success message
				const nodeCount = workflowData.nodes.length;
				const edgeCount = workflowData.edges.length;
			} catch (error) {
				console.error('Error loading workflow:', error);

				let errorMessage = 'Failed to load workflow file.';
				if (error instanceof SyntaxError) {
					errorMessage = 'Invalid JSON file format. Please check the file and try again.';
				} else if (error instanceof Error) {
					errorMessage = error.message;
				}

				alert({
					title: 'Error Loading Workflow',
					message: errorMessage,
					type: 'error',
				});
			}
		};

		input.click();
		setIsMenuOpen(false);
	};

	// Validate workflow
	const handleValidate = () => {
		const nodes = getNodes();
		const edges = getEdges();

		// Basic validation rules
		const hasStart = nodes.some(node => node.type === 'start');
		const hasEnd = nodes.some(node => node.type === 'end');
		const allNodesConnected = nodes.every(node => {
			return edges.some(edge => edge.source === node.id || edge.target === node.id);
		});

		// Show validation results
		const validationResults = [];
		if (!hasStart) validationResults.push('Missing Start node');
		if (!hasEnd) validationResults.push('Missing End node');
		if (!allNodesConnected) validationResults.push('Some nodes are not connected');

		if (validationResults.length === 0) {
			alert({
				title: 'Validation Passed',
				message: 'Workflow validation passed successfully!',
				type: 'success',
			});
		} else {
			alert({
				title: 'Validation Failed',
				message: `Validation failed:\n${validationResults.join('\n')}`,
				type: 'error',
			});
		}

		setIsMenuOpen(false);
	};

	// Test run simulation
	const handleTestRun = () => {
		const nodes = getNodes();
		const edges = getEdges();

		// Simulate workflow execution
		let currentNodeId = nodes.find(n => n.type === 'start')?.id;
		const executionPath: string[] = [];

		while (currentNodeId) {
			const node = getNode(currentNodeId);
			if (!node) break;

			executionPath.push(`${node.type}: ${node.data.label}`);

			// Find next node
			const nextEdge = edges.find(e => e.source === currentNodeId);
			currentNodeId = nextEdge?.target;

			if (node.type === 'end') break;
		}

		alert({
			title: 'Test Run Simulation',
			message: executionPath.join('\n➡️ '),
			type: 'info',
		});
		setIsMenuOpen(false);
	};

	// Load sample workflow
	const handleLoadSample = () => {
		setNodes(sampleWorkflow.nodes);
		setEdges(sampleWorkflow.edges);
		setTimeout(() => {
			fitView({ padding: 0.2 });
		}, 50);
		setIsMenuOpen(false);
	};

	// Handle pasting a node
	const handlePasteNode = () => {
		const pastedNode = pasteNode();
		if (pastedNode) {
			// Calculate position for pasted node (offset from current view center)
			const centerX = window.innerWidth / 2;
			const centerY = window.innerHeight / 2;

			// Update the node position
			pastedNode.position = { x: centerX, y: centerY };

			// Add the node to the workflow
			setNodes((nds: Node[]) => nds.concat(pastedNode));
		}
		setIsMenuOpen(false);
	};

	const handleAutoLayout = () => {
		const reactFlowInstance = { getNodes, getEdges, setNodes, fitView };
		applyAutoLayout(reactFlowInstance);
		setIsMenuOpen(false);
	};

	// Clear canvas
	const handleClearCanvas = () => {
		confirm({
			title: 'Clear Canvas',
			message: 'Are you sure you want to clear the canvas? This action cannot be undone.',
			confirmText: 'Clear',
			cancelText: 'Cancel',
			type: 'danger',
			onConfirm: () => {
				setNodes([]);
				setEdges([]);
				saveToHistory([], []);
			},
		});
		setIsMenuOpen(false);
	};

	// Edit workflow metadata
	const handleEditWorkflow = () => {
		onEditWorkflow();
		setIsMenuOpen(false);
	};

	// Open help menu
	const handleOpenHelp = () => {
		setIsHelpMenuOpen(true);
	};

	const menuItems: MenuItem[] = [
		{ icon: Save, label: 'Save Workflow', onClick: handleSave },
		{ icon: Upload, label: 'Load Workflow', onClick: handleLoadWorkflow },
		{ icon: Download, label: 'Export as JSON', onClick: handleExportJson },
		{ icon: CheckCircle, label: 'Validate Workflow', onClick: handleValidate },
		{ icon: Play, label: 'Test Run', onClick: handleTestRun },
		{ type: 'divider' },
		{ icon: Edit3, label: 'Edit Title & Description', onClick: handleEditWorkflow },
		{ type: 'divider' },
		{
			icon: Undo,
			label: 'Undo',
			onClick: () => {
				undo();
				setIsMenuOpen(false);
			},
			disabled: !canUndo,
		},
		{
			icon: Redo,
			label: 'Redo',
			onClick: () => {
				redo();
				setIsMenuOpen(false);
			},
			disabled: !canRedo,
		},
		{ type: 'divider' },
		{ icon: Zap, label: 'Load Sample Workflow', onClick: handleLoadSample },
		{ icon: Maximize, label: 'Auto Layout', onClick: handleAutoLayout },
		{ type: 'divider' },
		{
			icon: Trash2,
			label: 'Clear Canvas',
			onClick: handleClearCanvas,
			className: 'text-red-600',
		},
		{
			icon: Settings,
			label: 'Reset Workflow',
			onClick: () => {
				onResetWorkflow();
				setIsMenuOpen(false);
			},
			className: 'text-red-600',
		},
		{
			icon: Clipboard,
			label: 'Paste Node',
			onClick: handlePasteNode,
			disabled: !hasNodeInClipboard,
		},
	];

	// Get workflow status from metadata or infer from nodes if not available
	const getWorkflowStatus = () => {
		// For existing workflows, use the status from metadata
		if (workflowMetadata.status) {
			return (
				workflowMetadata.status.charAt(0).toUpperCase() + workflowMetadata.status.slice(1)
			);
		}

		// For new workflows, infer status from nodes
		const nodes = getNodes();
		if (nodes.length === 0) return 'Empty';

		const hasStartNode = nodes.some(
			node =>
				node.type === 'start' ||
				node.type.startsWith('start.') ||
				node.type === 'submission-form'
		);
		const hasEndNode = nodes.some(node => node.type === 'end');

		if (hasStartNode && hasEndNode) return 'Draft';
		return 'Incomplete';
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	};

	return (
		<>
			<div className="flex justify-between items-center px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 transition-colors">
				<div className="flex gap-3 items-center">
					{/* Back to Workflows Button */}
					<Link
						to="/flows"
						className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
						title="Back to workflows list"
					>
						<ArrowLeft className="w-4 h-4" />
						<span className="hidden sm:inline">Back to Workflows</span>
					</Link>

					{/* Workflow Title Section */}
					<div className="flex gap-2 items-center">
						<button
							onClick={handleEditWorkflow}
							className="flex gap-2 items-center px-2 py-1 rounded-lg transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 group"
							title="Click to edit workflow title and description"
						>
							<h1 className="text-lg font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
								{workflowMetadata.title}
							</h1>
							<Edit3 className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
						</button>
					</div>

					<div className="flex gap-2 items-center">
						<span
							className={`px-2 py-0.5 text-xs rounded font-medium ${
								getWorkflowStatus() === 'Draft'
									? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
									: getWorkflowStatus() === 'Empty'
									? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
									: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
							}`}
						>
							{getWorkflowStatus()}
						</span>

						{workflowMetadata.description && (
							<span
								className="max-w-xs text-xs text-gray-500 dark:text-gray-400 truncate"
								title={workflowMetadata.description}
							>
								{workflowMetadata.description}
							</span>
						)}
					</div>
				</div>

				<div className="flex gap-2 items-center">
					<div className="text-xs text-gray-500 dark:text-gray-400">
						Updated {formatDate(workflowMetadata.updatedAt)}
					</div>

					{/* Help Button */}
					<button
						onClick={handleOpenHelp}
						className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
						title="View help documentation"
					>
						<HelpCircle size={16} />
						Help
					</button>

					<div className="relative">
						<button
							className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
							onClick={() => setIsMenuOpen(!isMenuOpen)}
						>
							<Settings size={16} />
							Actions
						</button>

						{isMenuOpen && (
							<>
								<div
									className="fixed inset-0"
									onClick={() => setIsMenuOpen(false)}
								/>
								<div className="absolute right-0 z-50 py-1 mt-1 w-56 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg">
									{menuItems.map((item, index) =>
										'type' in item ? (
											<div
												key={index}
												className="my-1 h-px bg-gray-200 dark:bg-gray-700"
											/>
										) : (
											<button
												key={index}
												className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
													item.disabled
														? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
														: 'text-gray-700 dark:text-gray-200'
												} ${item.className || ''}`}
												onClick={() => {
													if (!item.disabled) {
														item.onClick();
													}
												}}
												disabled={item.disabled}
											>
												<item.icon size={16} />
												{item.label}
											</button>
										)
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>

			{/* Help Menu */}
			<HelpMenu isOpen={isHelpMenuOpen} onClose={() => setIsHelpMenuOpen(false)} />
		</>
	);
}
